/**
 * Comercial → Relatório Notas faturadas - Exclusivo (#MT-57E7612, #MT-CA07226, #MT-5311110).
 * Prévia JSON + CSV + PDF. Acesso via grupo RELATORIOS GERENCIAIS / Administradores.
 * Padrão UI: spinner msgbox (prévia) / barra progress (CSV·PDF); tabela estilo Kardex no #workarea.
 */
import api from '../api.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import progress from '../../versa/progress.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function defaultDateFirstOfMonth() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function defaultToday() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtQty(v) {
	const n = Number(v);
	if (!Number.isFinite(n)) return v == null ? '' : String(v);
	return n.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

function fmtMoney(v, digits = 2) {
	const n = Number(v);
	if (!Number.isFinite(n)) return v == null ? '' : String(v);
	return n.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function downloadBase64(b64, filename, mime) {
	const bin = atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
	const a = document.createElement('a');
	a.href = URL.createObjectURL(new Blob([bytes], { type: mime }));
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(a.href);
}

async function loadCompanies(selectEl) {
	const mb = msgbox({ animation: true, text: 'Carregando empresas…' });
	try {
		const res = await api.list_all_companies();
		if (!res.ok) {
			notify(await res.text(), 'Empresas');
			return;
		}
		let list;
		try {
			list = await res.json();
		} catch {
			notify('Resposta de empresas inválida.', 'Empresas');
			return;
		}
		if (!Array.isArray(list)) return;
		selectEl.innerHTML = '<option value="">— Selecione —</option>';
		for (const c of list) {
			const o = document.createElement('option');
			o.value = c.id;
			o.textContent = c.nick || c.name || c.id;
			selectEl.appendChild(o);
		}
	} finally {
		mb.destroy();
	}
}

/**
 * Carrega clientes da empresa no datalist (rótulo → id).
 * @returns {Promise<Map<string, string>>}
 */
async function loadCustomersDatalist(companyId, datalistEl) {
	const map = new Map();
	datalistEl.replaceChildren();
	if (!companyId) return map;
	try {
		const q = `?filter=company_id*eq*${encodeURIComponent(companyId)}&limit=5000`;
		const res = await api.list_customers(q);
		if (!res.ok) return map;
		let list;
		try {
			list = await res.json();
		} catch {
			return map;
		}
		if (!Array.isArray(list)) return map;
		const frag = document.createDocumentFragment();
		for (const c of list) {
			const nick = (c.nick || '').trim();
			const name = (c.name || '').trim();
			const label = [nick, name].filter(Boolean).join(' — ') || `Cliente #${c.id}`;
			const opt = document.createElement('option');
			opt.value = label;
			frag.appendChild(opt);
			map.set(label.toLowerCase(), String(c.id));
			if (nick) map.set(nick.toLowerCase(), String(c.id));
			if (name && name.toLowerCase() !== nick.toLowerCase()) {
				map.set(name.toLowerCase(), String(c.id));
			}
		}
		datalistEl.appendChild(frag);
	} catch (e) {
		console.error(e);
	}
	return map;
}

/**
 * Carrega nomes de produto da empresa no datalist (mesmo padrão do cliente).
 * O valor da opção é o nome — o filtro da API continua sendo “contém”.
 */
async function loadProductsDatalist(companyId, datalistEl) {
	datalistEl.replaceChildren();
	if (!companyId) return;
	try {
		const q = `?filter=company_id*eq*${encodeURIComponent(companyId)}&limit=5000`;
		const res = await api.list_products(q);
		if (!res.ok) return;
		let list;
		try {
			list = await res.json();
		} catch {
			return;
		}
		if (!Array.isArray(list)) return;
		const seen = new Set();
		const frag = document.createDocumentFragment();
		for (const p of list) {
			if (p?.is_disabled) continue;
			const name = (p.name || '').trim();
			if (!name) continue;
			const key = name.toLowerCase();
			if (seen.has(key)) continue;
			seen.add(key);
			const opt = document.createElement('option');
			opt.value = name;
			frag.appendChild(opt);
		}
		datalistEl.appendChild(frag);
	} catch (e) {
		console.error(e);
	}
}

function syncCustomerIdFromName(filters, labelToId) {
	const nameEl = $('#ibe-customer-name', filters);
	const idEl = $('#ibe-customer-id', filters);
	if (!nameEl || !idEl) return;
	const raw = (nameEl.value || '').trim();
	if (!raw) {
		return;
	}
	const id = labelToId.get(raw.toLowerCase());
	if (id) {
		idEl.value = id;
	} else {
		// Texto livre (trecho do nome): não usar ID antigo de seleção anterior.
		idEl.value = '';
	}
}

function collectParams(node, format) {
	const companyId = $('#ibe-company', node)?.value?.trim();
	if (!companyId) {
		notify('Selecione a empresa.');
		return null;
	}
	const df = $('#ibe-date-from', node)?.value?.trim();
	const dt = $('#ibe-date-to', node)?.value?.trim();
	if (!df || !dt) {
		notify('Informe o período (data inicial e final).');
		return null;
	}
	if (dt < df) {
		notify('A data final deve ser igual ou posterior à inicial.');
		return null;
	}
	const params = {
		format,
		date_from: df,
		date_to: dt,
	};
	const cnpj = $('#ibe-cnpj', node)?.value?.trim();
	if (cnpj) params.cnpj = cnpj;
	const cid = $('#ibe-customer-id', node)?.value?.trim();
	const cname = $('#ibe-customer-name', node)?.value?.trim();
	if (cid) {
		params.customer_id = cid;
	} else if (cname) {
		params.customer_name = cname;
	}
	const st = $('#ibe-status', node)?.value?.trim();
	if (st && st !== 'Todos') params.status = st;
	const sku = $('#ibe-sku', node)?.value?.trim();
	if (sku) params.sku = sku;
	const pname = $('#ibe-product-name', node)?.value?.trim();
	if (pname) params.product_name = pname;
	const p344 = $('#ibe-portaria-344', node)?.value?.trim();
	if (p344 && p344 !== 'Todos') params.portaria_344 = p344;
	if (format === 'json') {
		let lim = parseInt($('#ibe-limit', node)?.value || '200', 10);
		if (!Number.isFinite(lim) || lim < 1) lim = 200;
		if (lim > 5000) lim = 5000;
		params.limit = String(lim);
		params.offset = '0';
	}
	return { companyId, params };
}

const PREVIEW_COLS = [
	['data_emissao', 'Emissão', 'text'],
	['cliente', 'Cliente', 'text'],
	['cnpj', 'CNPJ/CPF', 'text'],
	['municipio', 'Município', 'text'],
	['uf', 'UF', 'center'],
	['nota_fiscal', 'NF', 'center'],
	['cfop', 'CFOP', 'center'],
	['sku', 'SKU', 'text'],
	['produto', 'Produto', 'text'],
	['lote', 'Lote', 'text'],
	['quantidade', 'Qtd', 'num'],
	['valor_unitario', 'Vlr unit.', 'money4'],
	['valor_total', 'Vlr total', 'money'],
	['status', 'Status', 'text'],
];

function cellText(row, key, kind) {
	const v = row?.[key];
	if (kind === 'num') return fmtQty(v);
	if (kind === 'money') return fmtMoney(v, 2);
	if (kind === 'money4') return fmtMoney(v, 4);
	return v == null ? '' : String(v);
}

/**
 * Monta a prévia só com textContent (sem innerHTML/template — template.innerHTML vinha vazio).
 */
function renderPreview(container, payload) {
	container.replaceChildren();

	const rows = Array.isArray(payload?.rows) ? payload.rows : [];
	const total = payload?.total;
	const totalLabel = total == null || total === '' ? '—' : String(total);

	if (!rows.length) {
		const p = document.createElement('p');
		p.className = 'muted mts';
		p.textContent = `Nenhuma linha no período (total: ${totalLabel}).`;
		container.appendChild(p);
		return;
	}

	const summary = document.createElement('p');
	summary.className = 'mts';
	const s1 = document.createElement('strong');
	s1.textContent = String(rows.length);
	const s2 = document.createElement('strong');
	s2.textContent = totalLabel;
	const filterSum = Number(payload?.valor_total_sum);
	const pageSumRaw = payload?.valor_total_page;
	const pageSum = Number.isFinite(Number(pageSumRaw))
		? Number(pageSumRaw)
		: rows.reduce((acc, r) => acc + (Number(r?.valor_total) || 0), 0);
	const truncated = payload?.truncated === true || (Number.isFinite(Number(total)) && rows.length < Number(total));
	summary.append('Exibindo ', s1, ' de ', s2, ' linha(s).');
	if (Number.isFinite(filterSum)) {
		summary.append(' Subtotal Vlr total: ', document.createElement('strong'));
		summary.lastChild.textContent = fmtMoney(filterSum, 2);
		if (truncated) {
			summary.append(' (todas as linhas do filtro; nesta tela: ');
			const sPage = document.createElement('strong');
			sPage.textContent = fmtMoney(pageSum, 2);
			summary.append(sPage, ').');
		} else {
			summary.append('.');
		}
	}
	container.appendChild(summary);

	const scroll = document.createElement('div');
	scroll.className = 'ibe-scroll mts';

	const table = document.createElement('table');
	table.className = 'ibe-table';

	const thead = document.createElement('thead');
	const hr = document.createElement('tr');
	for (const [, label, kind] of PREVIEW_COLS) {
		const th = document.createElement('th');
		if (kind === 'center') th.className = 'ibe-center';
		if (kind === 'num' || kind === 'money' || kind === 'money4') th.className = 'ibe-num';
		th.textContent = label;
		hr.appendChild(th);
	}
	thead.appendChild(hr);
	table.appendChild(thead);

	const tbody = document.createElement('tbody');
	const frag = document.createDocumentFragment();
	for (const r of rows) {
		const tr = document.createElement('tr');
		for (const [key, , kind] of PREVIEW_COLS) {
			const td = document.createElement('td');
			if (kind === 'center') td.className = 'ibe-center';
			if (kind === 'num' || kind === 'money' || kind === 'money4') td.className = 'ibe-num';
			td.textContent = cellText(r, key, kind);
			tr.appendChild(td);
		}
		frag.appendChild(tr);
	}
	tbody.appendChild(frag);
	table.appendChild(tbody);

	const moneyIdx = PREVIEW_COLS.findIndex(([key]) => key === 'valor_total');
	const tfoot = document.createElement('tfoot');
	const addFooterRow = (label, amount) => {
		const tr = document.createElement('tr');
		tr.className = 'ibe-subtotal';
		for (let i = 0; i < PREVIEW_COLS.length; i++) {
			const td = document.createElement('td');
			const [, , kind] = PREVIEW_COLS[i];
			if (kind === 'center') td.className = 'ibe-center';
			if (kind === 'num' || kind === 'money' || kind === 'money4') td.className = 'ibe-num';
			if (i === 0) td.textContent = label;
			else if (i === moneyIdx) td.textContent = fmtMoney(amount, 2);
			else td.textContent = '';
			tr.appendChild(td);
		}
		tfoot.appendChild(tr);
	};
	if (truncated && Number.isFinite(filterSum)) {
		addFooterRow('Subtotal (exibido)', pageSum);
		addFooterRow('Subtotal (filtro)', filterSum);
	} else {
		addFooterRow('Subtotal', Number.isFinite(filterSum) ? filterSum : pageSum);
	}
	table.appendChild(tfoot);
	scroll.appendChild(table);
	container.appendChild(scroll);
}

function setActionsDisabled(root, disabled) {
	for (const id of ['ibe-preview', 'ibe-csv', 'ibe-pdf']) {
		const btn = $(`#${id}`, root);
		if (btn) btn.disabled = !!disabled;
	}
}

export default async function invoices_billed_exclusive() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.ibe-hint { color: #555; font-size: 0.9rem; max-width: 52rem; }
			.ibe-scroll {
				overflow: auto;
				max-height: 65vh;
				border: 1px solid var(--color-border, #cbd5e1);
				border-radius: 2px;
				background: #fff;
			}
			.ibe-filters label { display: block; margin-top: 0.5rem; font-size: 0.9rem; }
			.ibe-filters .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; }
			.ibe-filters .row > label { flex: 1 1 11rem; }
			.ibe-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
			.ibe-filters input, .ibe-filters select { width: 100%; max-width: 18rem; box-sizing: border-box; }
			.ibe-filters .ibe-panel {
				margin-top: 0.75rem;
				padding: 0.75rem 1rem 1rem;
				background: var(--color-secondary, #f8fafc);
				border: 1px solid var(--color-border, #e2e8f0);
			}
			.ibe-table tfoot td { background: #f1f5f9; font-weight: 600; }
			.ibe-table .ibe-subtotal td { border-top: 2px solid var(--color-border, #94a3b8); }
		</style>
		<div class="title">Relatório Notas faturadas - Exclusivo</div>
		<p class="mts ibe-hint">
			Notas de saída no período de <strong>emissão</strong>, com filtros opcionais de
			<strong>nome do cliente</strong>, CNPJ/CPF, ID, <strong>status da nota</strong>,
			<strong>código SKU</strong>, <strong>descrição do produto</strong> e
			<strong>Portaria 344</strong> (cadastro de Produtos).
			Uma linha por produto e lote. A prévia e os arquivos (CSV/PDF) trazem o
			<strong>subtotal do Vlr total</strong> ao final. Acesso restrito ao grupo
			<strong>RELATORIOS GERENCIAIS</strong> (e Administradores).
		</p>
		<div class="ibe-filters mts" id="ibe-filters">
			<div class="ibe-panel">
				<div class="row">
					<label>Empresa<sup>*</sup>
						<select id="ibe-company" required></select>
					</label>
					<label>Emissão de<sup>*</sup>
						<input type="date" id="ibe-date-from" required value="${defaultDateFirstOfMonth()}">
					</label>
					<label>até<sup>*</sup>
						<input type="date" id="ibe-date-to" required value="${defaultToday()}">
					</label>
					<label>Status da nota
						<select id="ibe-status">
							<option value="Todos" selected>Todos</option>
							<option value="Autorizada">Autorizada</option>
							<option value="Cancelada">Cancelada</option>
							<option value="Inutilizada">Inutilizada</option>
							<option value="Pendente">Pendente</option>
							<option value="Desabilitada">Desabilitada</option>
							<option value="Rejeitada">Rejeitada</option>
						</select>
					</label>
				</div>
				<div class="row">
					<label>Nome do cliente (opcional)
						<input type="text" id="ibe-customer-name" list="ibe-customers-dl"
							placeholder="digite nome ou apelido" maxlength="120" autocomplete="off">
						<datalist id="ibe-customers-dl"></datalist>
					</label>
					<label>CNPJ/CPF (opcional)
						<input type="text" id="ibe-cnpj" placeholder="somente dígitos ou formatado" maxlength="18" autocomplete="off">
					</label>
					<label>ID cliente SIAL (opcional)
						<input type="number" id="ibe-customer-id" min="1" step="1" placeholder="ex.: 123">
					</label>
					<label>Limite prévia
						<input type="number" id="ibe-limit" min="1" max="5000" value="200" title="Padrão 200 (máx. 5000). Valores altos deixam a grade mais lenta.">
					</label>
				</div>
				<div class="row">
					<label>Código SKU (opcional)
						<input type="text" id="ibe-sku" placeholder="ex.: 041523" maxlength="60" autocomplete="off">
					</label>
					<label>Descrição do produto (opcional)
						<input type="text" id="ibe-product-name" list="ibe-products-dl"
							placeholder="digite o nome (ex.: Diazepam)" maxlength="120" autocomplete="off">
						<datalist id="ibe-products-dl"></datalist>
					</label>
					<label>Portaria 344
						<select id="ibe-portaria-344" title="Cruza o SKU da NF com o cadastro de Produtos (flag Portaria 344)">
							<option value="Todos" selected>Todos</option>
							<option value="only">Somente Portaria 344</option>
							<option value="exclude">Somente sem Portaria 344</option>
						</select>
					</label>
				</div>
				<div class="ibe-actions">
					<button type="button" class="button" id="ibe-preview">Prévia</button>
					<button type="button" class="button" id="ibe-csv">CSV</button>
					<button type="button" class="button" id="ibe-pdf">PDF</button>
				</div>
			</div>
		</div>
		<div id="ibe-preview-host" class="mts"></div>
	`;

	const filters = $('#ibe-filters', work);
	const previewHost = $('#ibe-preview-host', work);
	const companySel = $('#ibe-company', filters);
	const customersDl = $('#ibe-customers-dl', filters);
	const productsDl = $('#ibe-products-dl', filters);
	let customerLabelToId = new Map();

	await loadCompanies(companySel);

	async function refreshLookups() {
		const cid = companySel?.value?.trim();
		const mb = msgbox({ animation: true, text: 'Carregando clientes e produtos…' });
		try {
			const [cmap] = await Promise.all([
				loadCustomersDatalist(cid, customersDl),
				loadProductsDatalist(cid, productsDl),
			]);
			customerLabelToId = cmap;
		} finally {
			mb.destroy();
		}
	}

	companySel?.addEventListener('change', async () => {
		$('#ibe-customer-name', filters).value = '';
		$('#ibe-customer-id', filters).value = '';
		$('#ibe-product-name', filters).value = '';
		$('#ibe-sku', filters).value = '';
		await refreshLookups();
	});
	$('#ibe-customer-name', filters)?.addEventListener('change', () => {
		syncCustomerIdFromName(filters, customerLabelToId);
	});
	$('#ibe-customer-name', filters)?.addEventListener('blur', () => {
		syncCustomerIdFromName(filters, customerLabelToId);
	});

	async function runPreview() {
		const collected = collectParams(filters, 'json');
		if (!collected) return;
		const { companyId, params } = collected;
		setActionsDisabled(filters, true);
		const mb = msgbox({ animation: true, text: 'Gerando prévia do relatório…' });
		try {
			const res = await api.invoices_billed_exclusive_report(companyId, params);
			if (!res.ok) {
				notify((await res.text()) || `Erro ${res.status}`, 'Relatório');
				return;
			}
			const data = await res.json();
			if (!data || typeof data !== 'object' || Array.isArray(data)) {
				notify('Formato inesperado da API (esperado objeto com rows/total).', 'Relatório');
				return;
			}
			if (!Array.isArray(data.rows)) {
				notify('Resposta sem lista de linhas (rows).', 'Relatório');
				return;
			}
			renderPreview(previewHost, data);
			const shown = data.rows.length;
			const tot = data.total ?? shown;
			notify(`Prévia: ${shown} de ${tot} linha(s).`, 'Relatório');
		} catch (e) {
			console.error(e);
			notify(String(e?.message || e), 'Relatório');
		} finally {
			mb.destroy();
			setActionsDisabled(filters, false);
		}
	}

	async function runExport(format) {
		const collected = collectParams(filters, format);
		if (!collected) return;
		const { companyId, params } = collected;
		setActionsDisabled(filters, true);
		const label = format === 'csv' ? 'CSV' : 'PDF';
		const pg = progress({
			title: `Gerando ${label}…`,
			text: 'Consultando notas no período e montando o arquivo.',
			indeterminate: true,
		});
		try {
			pg.markStage('Aguardando resposta do servidor…');
			const res = await api.invoices_billed_exclusive_report(companyId, params);
			if (!res.ok) {
				notify((await res.text()) || `Erro ${res.status}`, 'Relatório');
				return;
			}
			pg.markStage('Processando arquivo…');
			const data = await res.json();
			if (format === 'csv' && data.csv) {
				downloadBase64(data.csv, data.filename || 'notas-faturadas.csv', 'text/csv;charset=utf-8');
				notify(`${data.rows || 0} linha(s) exportada(s).`, 'CSV');
				return;
			}
			if (format === 'pdf' && data.pdf) {
				downloadBase64(data.pdf, data.filename || 'notas-faturadas.pdf', 'application/pdf');
				notify(`${data.rows || 0} linha(s) no PDF.`, 'PDF');
				return;
			}
			notify('Resposta inesperada do servidor.', 'Relatório');
		} catch (e) {
			console.error(e);
			notify(String(e?.message || e), 'Relatório');
		} finally {
			pg.destroy();
			setActionsDisabled(filters, false);
		}
	}

	$('#ibe-preview', filters)?.addEventListener('click', () => runPreview());
	$('#ibe-csv', filters)?.addEventListener('click', () => runExport('csv'));
	$('#ibe-pdf', filters)?.addEventListener('click', () => runExport('pdf'));
}
